'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { CardSparkleIcon } from '@/components/aios-command/chat/card-sparkle-icon';
import type { SelectionOption, SelectionRequest } from '@/types/command-selection';

interface SelectionOptionTilesProps {
  selection: SelectionRequest;
  disabled?: boolean;
  onPick: (option: SelectionOption) => void;
}

/** Full-width option cards for `selection_request` / AIOS_ASK_USER_CHOICE. */
export function SelectionOptionTiles({
  selection,
  disabled = false,
  onPick,
}: SelectionOptionTilesProps) {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const isLocked = disabled || selectedValue != null;

  return (
    <div className="mt-3 flex w-full min-w-0 flex-col gap-2">
      {selection.prompt ? (
        <p className="text-sm text-muted-foreground">{selection.prompt}</p>
      ) : null}

      {selection.options.map((option, index) => {
        const showRecommended = Boolean(option.recommended);
        const isSelected = selectedValue === option.value;

        return (
          <button
            key={option.value || `${option.label}-${index}`}
            type="button"
            disabled={isLocked}
            onClick={() => {
              if (isLocked) return;
              setSelectedValue(option.value);
              onPick(option);
            }}
            className={cn(
              'w-full rounded-xl border bg-background px-4 py-3 text-left transition-colors',
              'border-border hover:border-primary/35 hover:bg-muted/40',
              'disabled:cursor-default disabled:opacity-60 disabled:hover:border-border disabled:hover:bg-background',
              isSelected && 'border-primary/50 bg-primary/5 disabled:opacity-100',
            )}
          >
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="text-sm font-semibold text-foreground">
                {option.label}
              </span>
              {showRecommended ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                  <CardSparkleIcon className="size-3.5" />
                  Recommended
                </span>
              ) : null}
            </div>
            {option.subtitle ? (
              <p className="mt-0.5 text-sm leading-snug text-muted-foreground">
                {option.subtitle}
              </p>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
